import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, peso, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { CustomerLayout } from "@/components/CustomerLayout";
import { STATUS_LABELS, STATUS_COLORS } from "@/components/OrderTicket";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function MyOrders() {
  const nav = useNavigate();
  const { add, clear } = useCart();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, []);

  const reorder = async (o) => {
    try {
      const { data } = await api.post(`/orders/${o.id}/reorder-check`);
      if (data.items.length === 0) { toast.error("None of these items are available right now."); return; }
      clear();
      const products = await api.get("/products").then((r) => r.data);
      data.items.forEach((it) => {
        const p = products.find((x) => x.id === it.product_id);
        if (p) add(p, it.quantity);
      });
      data.changes.forEach((c) => toast.info(c));
      if (data.changes.length === 0) toast.success("Items added to cart!");
      nav("/checkout");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <CustomerLayout>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="font-display text-3xl font-bold mb-6">My Orders</h1>
        {loading ? <p className="text-muted-foreground">Loading…</p> :
          orders.length === 0 ? (
            <div className="text-center py-16"><p className="text-muted-foreground">No orders yet.</p><Button className="mt-4 bg-[#1B5E20]" onClick={() => nav("/")} data-testid="start-order-btn">Start Ordering</Button></div>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.id} className="bg-white rounded-2xl border p-4 flex items-center justify-between gap-3" data-testid={`order-row-${o.order_number}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold text-[#1B5E20]">{o.order_number}</span>
                      <Badge style={{ background: STATUS_COLORS[o.status] }} className="text-white text-[10px]">{STATUS_LABELS[o.status]}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground truncate mt-1">{o.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{new Date(o.created_at).toLocaleString()} · {peso(o.grand_total)}</div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => nav(`/order/${o.id}`)} data-testid={`view-${o.order_number}`}>View</Button>
                    <Button size="sm" className="bg-[#1B5E20] hover:bg-[#144718]" onClick={() => reorder(o)} data-testid={`reorder-${o.order_number}`}>Reorder</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
    </CustomerLayout>
  );
}
